import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, ArrowRight, Calendar, Link2, AlertCircle,
  Loader2, Users, IndianRupee, Award, BarChart3,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, cycle) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}${cycle === "yearly" ? "/yr" : "/mo"}`;

// Benefits shown on the "become a partner" preview
const PARTNER_BENEFITS = [
  { icon: IndianRupee, text: "Earn commissions on every subscription you refer" },
  { icon: BarChart3,   text: "Track all your leads and earnings in real time" },
  { icon: Users,       text: "Build your own downline partner network" },
  { icon: Award,       text: "Dedicated partner portal with performance dashboard" },
];

export default function JoinLink() {
  const { shortCode } = useParams();
  const navigate      = useNavigate();
  const [link,    setLink]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!shortCode) return;
    axios.get(`${API}/api/join/${shortCode.toUpperCase()}`)
      .then(r => {
        const d = r.data;
        setLink(d);
        // Direct mode — bounce immediately without showing preview
        if (d.landing_mode === "direct") {
          navigate(buildDest(d, shortCode), { replace: true });
        }
      })
      .catch(err => {
        const s = err?.response?.status;
        setError(
          s === 404 ? "This referral link doesn't exist." :
          s === 410 ? "This referral link has expired or been deactivated." :
          "Unable to load this link. Please try again."
        );
      })
      .finally(() => setLoading(false));
  }, [shortCode, navigate]);

  const buildDest = (d, code) => {
    const c = (code || shortCode || "").toUpperCase();
    if (d.link_type === "partner") {
      return `/partner/register?ref=${encodeURIComponent(d.partner_id)}&link_code=${encodeURIComponent(c)}`;
    }
    // prospect → tenant registration / subscription checkout
    return `/register?plan_id=${encodeURIComponent(d.plan_id)}`
      + `&cycle=${encodeURIComponent(d.billing_cycle)}`
      + `&ref=${encodeURIComponent(d.partner_id)}`
      + `&link_code=${encodeURIComponent(c)}`;
  };

  const handleGetStarted = () => {
    if (!link) return;
    axios.post(`${API}/api/join/${shortCode.toUpperCase()}/track-registration`).catch(() => {});
    navigate(buildDest(link, shortCode));
  };

  /* ── Loading / Error ─────────────────────────────────────────── */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 text-primary animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-sm w-full bg-card border border-border rounded-2xl p-8 text-center shadow-lg">
        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-5 h-5 text-red-500" />
        </div>
        <h2 className="text-lg font-semibold text-foreground mb-2">Link Unavailable</h2>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  if (!link || link.landing_mode === "direct") return null;

  const isPartner = link.link_type === "partner";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">

        {/* Referred-by badge */}
        <div className="flex items-center justify-center mb-6">
          <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground bg-muted/50 border border-border px-3 py-1.5 rounded-full">
            <Link2 className="w-3 h-3" />
            {isPartner ? "Partner invitation from" : "Referred by"}&nbsp;
            <strong className="text-foreground">{link.partner_name}</strong>
          </span>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xl">

          {/* ── PROSPECT card header ───────────────────────── */}
          {!isPartner && (
            <div className="bg-[#1e3a8a] px-7 py-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-blue-300 mb-1">
                {link.billing_cycle === "yearly" ? "Annual" : "Monthly"} Plan
              </p>
              <h1 className="text-2xl font-bold text-white">{link.plan_name}</h1>
              <p className="text-3xl font-bold text-white mt-3">
                {fmt(link.price_inr, link.billing_cycle)}
              </p>
              {link.billing_cycle === "yearly" && (
                <p className="text-[11px] text-blue-300 mt-1">Billed annually · Save up to 20%</p>
              )}
            </div>
          )}

          {/* ── PARTNER card header ───────────────────────── */}
          {isPartner && (
            <div className="bg-[#1e3a8a] px-7 py-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-blue-300 mb-1">Partner Invitation</p>
              <h1 className="text-2xl font-bold text-white">Become a Law OS Partner</h1>
              <p className="text-[13px] text-blue-200 mt-2 leading-relaxed">
                Join our partner network and earn commissions by referring law firms to Law OS.
              </p>
            </div>
          )}

          {/* ── Features / Benefits ──────────────────────── */}
          <div className="px-7 py-5 border-b border-border">
            {!isPartner && (
              <>
                {link.plan_description && (
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{link.plan_description}</p>
                )}
                {link.features?.length > 0 && (
                  <ul className="space-y-2.5">
                    {link.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {isPartner && (
              <ul className="space-y-3">
                {PARTNER_BENEFITS.map(({ icon: Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── CTA ──────────────────────────────────────── */}
          <div className="px-7 py-5">
            <button
              onClick={handleGetStarted}
              data-testid="join-get-started-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#1e3a8a] text-white py-3.5 rounded-xl font-semibold text-[15px] hover:opacity-90 active:scale-[0.98] transition-all"
            >
              {isPartner ? "Apply to Become a Partner" : "Get Started"}
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-center text-[11px] text-muted-foreground mt-3 flex items-center justify-center gap-1.5">
              {isPartner
                ? <><Users className="w-3 h-3" /> Free to join · Earn from day one</>
                : <><Calendar className="w-3 h-3" /> No commitment · Cancel anytime</>
              }
            </p>
          </div>
        </div>

        <p className="text-center text-[11px] text-muted-foreground mt-5">
          Powered by <strong className="text-foreground">Law OS — Inno Aim Private Limited</strong>
        </p>
      </div>
    </div>
  );
}
